/**
 * Phase 5.2 — AI Guided Task Wizard
 *
 * Like AIGuidedInterview for incidents but optimized for task creation:
 *   1. User types a one-line goal ("I need to set up workers' comp")
 *   2. AI asks 3-6 refining questions one at a time
 *   3. AI drafts a full task: title + category + priority + due date +
 *      subtasks + notes + a suggested reminder date
 *   4. User reviews, edits any field, clicks Create → task is saved
 *      (with its subtasks + reminder) via the standard CRUD endpoints.
 *
 * Manual mode is always available — the parent page's "Create Task"
 * button still opens the plain form. This modal is strictly additive.
 */
import { useEffect, useState } from 'react';
import Icon from '../ui/Icon';
import { planTasksApi, PlanAIDraftResult, PlanTaskGroup } from '../../lib/api';

type QA = { question: string; answer: string };
type Stage = 'goal' | 'asking' | 'drafting' | 'review' | 'saving' | 'error';

interface Props {
  groups: PlanTaskGroup[];
  onCreated: () => void;
  onClose: () => void;
  /** Optional pre-filled goal. If provided, the wizard starts at the
   *  goal stage with this text already typed — the user can hit Start
   *  immediately or edit it. Used by the AI Chat action buttons. */
  initialGoal?: string;
}

export default function AITaskWizard({ groups, onCreated, onClose, initialGoal }: Props) {
  const [stage, setStage] = useState<Stage>('goal');
  const [goal, setGoal] = useState(initialGoal ?? '');
  const [pairs, setPairs] = useState<QA[]>([]);
  const [currentQ, setCurrentQ] = useState('');
  const [currentA, setCurrentA] = useState('');
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<PlanAIDraftResult | null>(null);
  const [editedSubtasks, setEditedSubtasks] = useState<string[]>([]);
  const [groupId, setGroupId] = useState<string>('');
  const [reminderOn, setReminderOn] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function startInterview() {
    if (goal.trim().length < 3) { setError('Type a goal first.'); return; }
    setError(null);
    await fetchNextQuestion([]);
  }

  async function fetchNextQuestion(answers: QA[]) {
    setLoading(true); setError(null);
    try {
      const { data } = await planTasksApi.aiNextQuestion({ goal, answers });
      if (data.done || !data.question) {
        await fetchDraft(answers);
      } else {
        setCurrentQ(data.question); setCurrentA(''); setStage('asking');
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'AI unavailable.');
      setStage('error');
    } finally { setLoading(false); }
  }

  async function fetchDraft(answers: QA[]) {
    setStage('drafting'); setLoading(true); setError(null);
    try {
      const { data } = await planTasksApi.aiDraft({ goal, answers });
      setDraft(data);
      setEditedSubtasks(data.subtasks);
      setStage('review');
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'AI unavailable.');
      setStage('error');
    } finally { setLoading(false); }
  }

  function submitAnswer() {
    if (!currentA.trim()) return;
    const next = [...pairs, { question: currentQ, answer: currentA.trim() }];
    setPairs(next);
    void fetchNextQuestion(next);
  }
  function finishEarly() {
    const next = currentA.trim()
      ? [...pairs, { question: currentQ, answer: currentA.trim() }]
      : pairs;
    if (next.length === 0) { setError('Answer at least one question before drafting.'); return; }
    setPairs(next);
    void fetchDraft(next);
  }

  async function saveTask() {
    if (!draft) return;
    setStage('saving'); setError(null);
    // Phase 6.6 QA aid — log the exact draft at submit time so a lost
    // edit shows up in the console instead of being invisible.
    console.log('[ai-task-wizard] saving draft:', {
      title: draft.title, category: draft.category, priority: draft.priority,
      due_date: draft.due_date, notes_length: draft.notes?.length ?? 0,
      subtask_count: editedSubtasks.length, group_id: groupId || null,
    });
    try {
      // 1. Create task
      const t = await planTasksApi.createTask({
        title: draft.title,
        category: draft.category,
        priority: draft.priority,
        due_date: draft.due_date,
        notes: draft.notes,
        group_id: groupId || null,
      });
      const taskId = t.data.id;

      // 2. Create subtasks
      for (const st of editedSubtasks.filter(s => s.trim())) {
        await planTasksApi.addSubtask(taskId, { title: st.trim() });
      }

      // 3. Create reminder if enabled + there's a due date + suggested days
      if (reminderOn && draft.due_date && draft.suggested_reminder_days != null) {
        const due = new Date(draft.due_date + 'T09:00:00');
        due.setDate(due.getDate() - draft.suggested_reminder_days);
        await planTasksApi.addReminder(taskId, {
          remind_at: due.toISOString(),
          message: `Reminder: ${draft.title} due ${draft.due_date}`,
        });
      }

      onCreated();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Save failed.');
      setStage('review');
    }
  }

  // Focus the answer field when a new question arrives
  useEffect(() => {
    if (stage === 'asking' && currentQ) {
      const t = document.getElementById('ai-task-answer-input');
      if (t) (t as HTMLTextAreaElement).focus();
    }
  }, [stage, currentQ]);

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Icon name="sparkles" size={21} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>AI Task Wizard</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              {stage === 'goal' && 'What are you trying to get done?'}
              {stage === 'asking' && 'Answer a few questions — skip to manual anytime.'}
              {stage === 'drafting' && 'Drafting your task…'}
              {stage === 'review' && 'Review + edit before saving.'}
              {stage === 'saving' && 'Saving task and subtasks…'}
              {stage === 'error' && 'Something went wrong.'}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        {error && (
          <div style={errBox}>
            {error}
          </div>
        )}

        {/* Stage 1: goal */}
        {stage === 'goal' && (
          <>
            <label style={lbl}>Goal</label>
            <textarea
              autoFocus
              style={{ ...field, minHeight: 80, resize: 'vertical' }}
              value={goal}
              onChange={e => setGoal(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) void startInterview(); }}
              placeholder="e.g. Set up workers' comp insurance for new staff by end of May"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <button onClick={onClose} style={ghostBtn}>Cancel</button>
              <button onClick={() => void startInterview()} disabled={!goal.trim() || loading} style={primaryBtn}>
                {loading ? 'Thinking…' : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="sparkles" size={14} /> Start</span>}
              </button>
            </div>
          </>
        )}

        {/* Stage 2: Q&A */}
        {stage === 'asking' && (
          <>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginBottom: 6 }}>
              Question {pairs.length + 1} · {pairs.length === 0 ? 'just starting' : `${pairs.length} answered`}
            </div>
            <div style={{ padding: 14, background: 'var(--ai-tint)', border: '1px solid var(--ai-border)', borderRadius: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ai)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>AI asks</div>
              <div style={{ fontSize: 14, color: 'var(--text-strong)' }}>{loading ? 'Thinking…' : currentQ}</div>
            </div>
            <textarea
              id="ai-task-answer-input"
              style={{ ...field, minHeight: 80, resize: 'vertical' }}
              value={currentA}
              onChange={e => setCurrentA(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submitAnswer(); }}
              placeholder="Your answer…"
              disabled={loading}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
              <button onClick={onClose} style={ghostBtn}>Skip to manual</button>
              <div style={{ display: 'flex', gap: 8 }}>
                {pairs.length >= 2 && (
                  <button onClick={finishEarly} style={ghostBtn} disabled={loading} title="Draft now with the answers I've given">Draft now</button>
                )}
                <button onClick={submitAnswer} disabled={!currentA.trim() || loading} style={primaryBtn}>Next →</button>
              </div>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              Tip: Ctrl+Enter to submit. AI stops asking once it has enough.
            </div>
          </>
        )}

        {/* Stage 3: drafting */}
        {stage === 'drafting' && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>
            Generating task with subtasks…
          </div>
        )}

        {/* Stage 4: review */}
        {stage === 'review' && draft && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {/* Phase 6.6 QA fix — every setDraft uses the functional
                  form `setDraft(prev => ({...prev, key: val}))` instead
                  of the object form `setDraft({...draft, key: val})`.
                  The object form captures the `draft` variable from the
                  render closure; if a state update is still in-flight
                  when the user clicks "Create task", the edit gets lost
                  (QA saw a due-date edit disappear). Functional form
                  always reads the freshest state. */}
              <div style={{ gridColumn: 'span 2' }}>
                <label style={lbl}>Title</label>
                <input style={field} value={draft.title} onChange={e => setDraft(prev => prev ? ({ ...prev, title: e.target.value }) : prev)} />
              </div>
              <div>
                <label style={lbl}>Category</label>
                <input style={field} value={draft.category} onChange={e => setDraft(prev => prev ? ({ ...prev, category: e.target.value }) : prev)} />
              </div>
              <div>
                <label style={lbl}>Priority</label>
                <select style={field} value={draft.priority} onChange={e => setDraft(prev => prev ? ({ ...prev, priority: e.target.value as PlanAIDraftResult['priority'] }) : prev)}>
                  <option value="High">High</option><option value="Medium">Medium</option><option value="Low">Low</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Due date</label>
                <input type="date" style={field} value={draft.due_date ?? ''} onChange={e => setDraft(prev => prev ? ({ ...prev, due_date: e.target.value || null }) : prev)} />
              </div>
              <div>
                <label style={lbl}>Group</label>
                <select style={field} value={groupId} onChange={e => setGroupId(e.target.value)}>
                  <option value="">— No group —</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={lbl}>Notes</label>
                <textarea style={{ ...field, minHeight: 60, resize: 'vertical' }} value={draft.notes} onChange={e => setDraft(prev => prev ? ({ ...prev, notes: e.target.value }) : prev)} />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={lbl}>Subtasks ({editedSubtasks.length})</label>
                {editedSubtasks.map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 5 }}>
                    <input style={{ ...field, flex: 1 }} value={s} onChange={e => {
                      const next = [...editedSubtasks]; next[i] = e.target.value; setEditedSubtasks(next);
                    }} />
                    <button onClick={() => setEditedSubtasks(editedSubtasks.filter((_, j) => j !== i))} style={{ ...ghostBtn, color: 'var(--danger)' }}>×</button>
                  </div>
                ))}
                <button onClick={() => setEditedSubtasks([...editedSubtasks, ''])} style={{ ...ghostBtn, marginTop: 4 }}>+ Add step</button>
              </div>
              {draft.due_date && draft.suggested_reminder_days != null && (
                <label style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text)', cursor: 'pointer', padding: '8px 10px', background: 'var(--good-tint)', border: '1px solid var(--good-border)', borderRadius: 8 }}>
                  <input type="checkbox" checked={reminderOn} onChange={e => setReminderOn(e.target.checked)} />
                  Create a reminder {draft.suggested_reminder_days} day{draft.suggested_reminder_days !== 1 ? 's' : ''} before due date
                </label>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 14 }}>
              <button onClick={() => void fetchDraft(pairs)} style={ghostBtn} disabled={loading}>Regenerate</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onClose} style={ghostBtn}>Discard</button>
                <button onClick={() => void saveTask()} style={primaryBtn} disabled={!draft.title.trim()}>
                  Create task
                </button>
              </div>
            </div>
          </>
        )}

        {stage === 'saving' && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--text-muted)' }}>Saving…</div>
        )}

        {stage === 'error' && (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ color: 'var(--danger)', marginBottom: 10 }}>{error}</div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
              <button onClick={() => setStage('goal')} style={ghostBtn}>Start over</button>
              <button onClick={onClose} style={ghostBtn}>Skip to manual</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const overlay: React.CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const card: React.CSSProperties = { background: 'var(--surface)', borderRadius: 14, padding: 22, width: '100%', maxWidth: 560, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const field: React.CSSProperties = { width: '100%', padding: '7px 10px', border: '1.5px solid var(--border)', borderRadius: 7, fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'var(--surface)', fontFamily: 'inherit', color: 'var(--text-strong)' };
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 };
const primaryBtn: React.CSSProperties = { padding: '9px 18px', background: 'var(--ai)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' };
const ghostBtn: React.CSSProperties = { padding: '7px 14px', background: 'var(--surface-subtle)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12, fontWeight: 600, color: 'var(--text)', cursor: 'pointer' };
const closeBtn: React.CSSProperties = { background: 'none', border: 'none', fontSize: 22, color: 'var(--text-muted)', cursor: 'pointer' };
const errBox: React.CSSProperties = { padding: 10, background: 'var(--danger-tint)', border: '1px solid var(--danger-border)', color: 'var(--danger-fg)', borderRadius: 8, fontSize: 12, marginBottom: 10 };
